// Tile usado por el panel de Transiciones. Reproduce en loop una
// previsualización de cómo queda la transición elegida entre dos clips
// "A" y "B" — cada clip se representa como un panel de color sólido con
// un letrero. Mismo patrón visual que `AnimationPreviewTile`:
// IntersectionObserver para pausar fuera del viewport, rAF para el reloj
// local, y aspect ratio que matchea el del proyecto para que el preview
// se sienta como el editor.
//
// La animación delega en las mismas funciones de cómputo que usa el
// renderer del preview real (`computeIncomingTransitionState` /
// `computeOutgoingTransitionState`), así garantizamos que lo que ves en
// el tile es exactamente lo que vas a ver en el video.

import React, { useEffect, useRef, useState } from 'react';

type AspectRatio = '16:9' | '9:16' | '4:3';

interface State {
  opacity: number;
  translateXPct?: number;
  translateYPct?: number;
  scale?: number;
  blurPx?: number;
  blurDirection?: 'x' | 'y';
  wipeShowLeftPct?: number;
  wipeShowRightPct?: number;
  slideInsetPct?: number;
  slideRoundPx?: number;
  stretchX?: number;
  stretchY?: number;
}

interface Props {
  type: string;
  label: string;
  // Inyectadas desde VideoEditor para no duplicar la lógica de animación.
  computeIncoming: (type: string, t: number) => State;
  computeOutgoing: (type: string, t: number) => State;
  aspectRatio: AspectRatio;
  isActive: boolean;
  onClick: () => void;
  // For overlay-style transitions (e.g. flash, light leak), the URL of
  // the MP4 to composite on top of the A/B panels via screen-blend.
  // The video's currentTime is synced to the tile's transition progress
  // so the preview matches the actual playback render.
  overlayUrl?: string;
}

// Loop total = transición + dos "holds" (uno antes y uno después) para
// que el ojo distinga claramente A → transición → B antes de reiniciar.
const HOLD_BEFORE_SEC = 0.6;
const TRANSITION_SEC = 1.0;
const HOLD_AFTER_SEC = 0.7;
const LOOP_SEC = HOLD_BEFORE_SEC + TRANSITION_SEC + HOLD_AFTER_SEC;

// Overlay videos start a touch BEFORE the A/B transition and stop a
// touch AFTER, so the bright peak of the effect (light leak, flash,
// scratches, etc.) lines up nicely with the cut at the midpoint and the
// effect's lead-in / tail-out are visible too. Without these margins
// the overlay starts exactly when the transition does and the very
// first frame of the effect can feel abrupt.
const OVERLAY_LEAD_SEC = 0.2;
const OVERLAY_TRAIL_SEC = 0.25;

export const TransitionPreviewTile: React.FC<Props> = ({
  type,
  label,
  computeIncoming,
  computeOutgoing,
  aspectRatio,
  isActive,
  onClick,
  overlayUrl,
}) => {
  const tileRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLVideoElement>(null);
  const [visible, setVisible] = useState(false);
  const [localTime, setLocalTime] = useState(0);

  useEffect(() => {
    const el = tileRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      entries => {
        for (const e of entries) setVisible(e.isIntersecting);
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      setLocalTime(t => {
        const next = t + dt;
        return next >= LOOP_SEC ? 0 : next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  // Map local loop time → transition progress (0..1) AND a phase flag.
  // Phase A: hold (showing only A, B hidden — A in clean rest state)
  // Phase B: transition (from incoming/outgoing computes)
  // Phase C: hold (showing only B, A hidden — B in clean rest state)
  //
  // The phase flag is needed because some transitions (e.g. slides) have
  // non-zero blur at t=0 / t=1 due to velocity-tracked motion blur. If we
  // simply pinned progress at 0/1 during the holds, A would render fully
  // blurred while just "sitting there" before the transition begins. So
  // during holds we override to a clean state instead of using the
  // transition compute output.
  const { progress, phase }: { progress: number; phase: 'before' | 'mid' | 'after' } = (() => {
    if (localTime < HOLD_BEFORE_SEC) return { progress: 0, phase: 'before' };
    if (localTime > HOLD_BEFORE_SEC + TRANSITION_SEC) return { progress: 1, phase: 'after' };
    return { progress: (localTime - HOLD_BEFORE_SEC) / TRANSITION_SEC, phase: 'mid' };
  })();

  // Overlay video has its own phase that's slightly wider than the A/B
  // transition phase (see OVERLAY_LEAD_SEC / OVERLAY_TRAIL_SEC) so the
  // effect's lead-in and tail-out are visible.
  const overlayPhase: 'pre' | 'play' | 'post' = (() => {
    const playStart = HOLD_BEFORE_SEC - OVERLAY_LEAD_SEC;
    const playEnd = HOLD_BEFORE_SEC + TRANSITION_SEC + OVERLAY_TRAIL_SEC;
    if (localTime < playStart) return 'pre';
    if (localTime > playEnd) return 'post';
    return 'play';
  })();

// Drive the overlay video at 1× rate during the transition phase, with
  // the `loop` attribute on the <video> handling continuous playback
  // automatically. Earlier we tried scaling `playbackRate` to fit the
  // overlay's full duration into TRANSITION_SEC, but for the longer
  // overlays (VHS ~10s, Vintage ~14s) that meant 10–14× rates which
  // browsers can't actually render frame-by-frame — they fall back to
  // just decoding keyframes, producing the "2 frames flickering" effect.
  //
  //   • Phase 'before' → rewind to 0 + pause (sits on opening frame,
  //     which is black for these overlays so it's invisible via screen).
  //   • Phase 'mid'    → ensure playing at 1× rate. The video may not
  //     play through completely if it's longer than TRANSITION_SEC, but
  //     the user sees a real, smoothly-decoded clip, which is what we
  //     want for a preview.
  //   • Phase 'after'  → pause wherever it ended up (still black via
  //     screen-blend on the end frame).
  //
  // Depending only on `phase` (not `progress`) means this effect runs
  // exactly 3× per loop instead of ~60×, avoiding any chance of
  // re-triggering pause/play on every rAF tick.
  useEffect(() => {
    const el = overlayRef.current;
    if (!el || !overlayUrl) return;
    if (overlayPhase === 'play') {
      if (el.playbackRate !== 1) el.playbackRate = 1;
      if (el.paused) el.play().catch(() => {});
      return;
    }
    if (!el.paused) el.pause();
    if (overlayPhase === 'pre' && el.currentTime > 0.05) {
      try { el.currentTime = 0; } catch {}
    }
  }, [overlayPhase, overlayUrl]);

  // During the hold phases we DON'T run the transition compute funcs —
  // those can return non-zero blur/transforms at t=0/t=1 (e.g. slides
  // peak their motion blur exactly at velocity start). Instead, paint
  // a clean rest state: A fully visible before the transition, B fully
  // visible after.
  const REST_VISIBLE: State = { opacity: 1 };
  const REST_HIDDEN: State = { opacity: 0 };
  const outgoingState =
    phase === 'before' ? REST_VISIBLE
    : phase === 'after' ? REST_HIDDEN
    : computeOutgoing(type, progress);
  const incomingState =
    phase === 'before' ? REST_HIDDEN
    : phase === 'after' ? REST_VISIBLE
    : computeIncoming(type, progress);

  // Aspect ratio padding-top trick (same as AnimationPreviewTile).
  const aspectPaddingTop =
    aspectRatio === '9:16' ? '177.78%' : aspectRatio === '4:3' ? '75%' : '56.25%';

  // Stock images used as the A/B panels in the preview. We pick the
  // variant matching the project's aspect ratio so the preview's framing
  // looks like the actual editor output. 4:3 ("post") is the closest
  // available match for a square-ish frame.
  const stockSuffix =
    aspectRatio === '9:16' ? 'vertical' : aspectRatio === '4:3' ? 'post' : 'landscape';
  const imgA = `/images/transition_stock_images/img1-${stockSuffix}.PNG`;
  const imgB = `/images/transition_stock_images/img2-${stockSuffix}.PNG`;

  // Compose transforms the same way the actual playback render does,
  // so what you see in the tile matches the video. Supports uniform
  // scale and per-axis stretch (where the latter takes priority).
  const transformFor = (s: State): string | undefined => {
    const parts: string[] = [];
    if (s.translateXPct != null && s.translateXPct !== 0) parts.push(`translateX(${s.translateXPct}%)`);
    if (s.translateYPct != null && s.translateYPct !== 0) parts.push(`translateY(${s.translateYPct}%)`);
    const sx = s.stretchX ?? s.scale;
    const sy = s.stretchY ?? s.scale;
    if (sx != null || sy != null) {
      const x = sx ?? 1;
      const y = sy ?? 1;
      if (x === y) parts.push(`scale(${x})`);
      else parts.push(`scale(${x}, ${y})`);
    }
    return parts.length > 0 ? parts.join(' ') : undefined;
  };
  // Tile-local filter id so multiple tiles' SVG filters don't collide.
  const filterId = `tr-mb-${type}`;
  const filterFor = (s: State): string | undefined => {
    if (s.blurPx == null || s.blurPx < 0.1) return undefined;
    if (s.blurDirection === 'x' || s.blurDirection === 'y') return `url(#${filterId})`;
    return `blur(${s.blurPx}px)`;
  };
  const clipPathFor = (s: State): string | undefined => {
    if (s.wipeShowLeftPct != null) {
      const v = Math.max(0, Math.min(100, s.wipeShowLeftPct));
      return `inset(0 ${100 - v}% 0 0)`;
    }
    if (s.wipeShowRightPct != null) {
      const v = Math.max(0, Math.min(100, s.wipeShowRightPct));
      return `inset(0 0 0 ${100 - v}%)`;
    }
    if (s.slideInsetPct != null && s.slideInsetPct > 0) {
      const inset = Math.max(0, Math.min(45, s.slideInsetPct));
      const round = Math.max(0, s.slideRoundPx ?? 0);
      return `inset(${inset}% round ${round}px)`;
    }
    return undefined;
  };
  // Wipe-line position for the `reveal` preview. We derive it from the
  // incoming state's wipeShowLeftPct (which directly equals the line's
  // position from the left).
  const wipeLinePct = incomingState.wipeShowLeftPct;
  // Active blur (both incoming and outgoing share value during a slide
  // so we just pick whichever is non-zero).
  const activeBlur = incomingState.blurPx ?? outgoingState.blurPx ?? 0;
  const activeBlurDir = incomingState.blurDirection || outgoingState.blurDirection || null;

  return (
    <button
      ref={tileRef}
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`group w-full text-left rounded-md bg-surface-card border ${
        isActive ? 'border-brand-primary ring-2 ring-brand-primary/30' : 'border-black/[0.06]'
      } shadow-sm hover:border-brand-primary/30 hover:shadow-md hover:-translate-y-px transition-all overflow-hidden`}
    >
      {/* Preview area */}
      <div className="relative w-full bg-black overflow-hidden" style={{ paddingTop: aspectPaddingTop }}>
        <div className="absolute inset-0">
          {/* Tile-local SVG filter for directional motion blur. Only
              rendered while there's actual blur to apply, otherwise the
              browser still has to resolve the URL filter every frame. */}
          {activeBlur > 0.1 && activeBlurDir && (
            <svg aria-hidden style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }}>
              <filter id={filterId} x="-10%" y="-10%" width="120%" height="120%">
                <feGaussianBlur stdDeviation={activeBlurDir === 'x' ? `${activeBlur} 0` : `0 ${activeBlur}`} />
              </filter>
            </svg>
          )}
          {visible ? (
            <>
              {/* Clip A — outgoing. Uses stock img1 for the project's
                  aspect ratio so the preview shows the transition over a
                  real frame rather than a flat color. */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${imgA})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  opacity: outgoingState.opacity,
                  transform: transformFor(outgoingState),
                  filter: filterFor(outgoingState),
                  clipPath: clipPathFor(outgoingState),
                }}
              />
              {/* Clip B — incoming, uses stock img2. */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${imgB})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  opacity: incomingState.opacity,
                  transform: transformFor(incomingState),
                  filter: filterFor(incomingState),
                  clipPath: clipPathFor(incomingState),
                }}
              />
              {/* Reveal wipe line (only present during the `reveal`
                  transition; positioned at the boundary between A and B). */}
              {wipeLinePct != null && wipeLinePct > 0 && wipeLinePct < 100 && (
                <div
                  className="absolute top-0 bottom-0 pointer-events-none"
                  style={{
                    left: `${wipeLinePct}%`,
                    width: 3,
                    transform: 'translateX(-1.5px)',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 0 10px 2px rgba(255,255,255,0.7)',
                  }}
                />
              )}
              {/* Overlay video for overlay-style transitions (flash,
                  light leak, etc). Composited via screen-blend on top
                  of A and B so the black background is treated as
                  transparent. The currentTime is seeked to follow the
                  tile's progress (see useEffect above). */}
              {overlayUrl && (
                <video
                  ref={overlayRef}
                  src={overlayUrl}
                  className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                  style={{ mixBlendMode: 'screen' }}
                  muted
                  playsInline
                  preload="auto"
                  loop
                />
              )}
            </>
          ) : null}
        </div>
      </div>
      {/* Label */}
      <div className="px-2 py-1.5">
        <p className="text-[12.5px] font-medium text-brand-black/85 truncate">{label}</p>
      </div>
    </button>
  );
};

export default TransitionPreviewTile;
